/**
 * 退款幂等键（契约 §1.5 / §13.6；附录 A9：**必填**，缺失或不是 uuid ⇒ 400 `VALIDATION_FAILED`）。
 *
 * ## 为什么不用时间戳或自增计数
 *
 * 服务端按这个键去重（`refunds.idempotency_key` 上有唯一索引，唯一索引是权威守卫），
 * 因此键必须跨设备、跨会话唯一。时间戳在「双击确认」那种同毫秒提交里会撞车 ——
 * 撞车的后果不是报错，而是第二次点击被判成重放、用户以为退了两次。
 *
 * ## 为什么必须有兜底
 *
 * `crypto.randomUUID()` 只在**安全上下文**里可用：社团在展会场馆用局域网 IP
 * （`http://192.168.x.x`）访问时 `window.isSecureContext === false`，`crypto.randomUUID`
 * 是 `undefined`（而 `getRandomValues` 仍可用）。没有兜底的话，退款按钮在那个环境下会抛
 * `TypeError: crypto.randomUUID is not a function` —— 功能直接不可用。
 *
 * 两级兜底产出与 `randomUUID` **同形态**的 v4 串：服务端的 `@IsUUID()` 一视同仁。
 *
 * ## 谁负责「一次打开只生成一次」
 *
 * 本函数只保证「调用一次 = 一个合法且唯一的键」；**生成时机**归调用方：
 * 退款弹窗在**挂载时**取一次键（`useState(() => newIdempotencyKey())`），页面用
 * `key={orderId:nonce}` 重挂载它 ⇒ 打开换新键、同一次打开内的重试复用旧键。
 * 放在点击处生成（或放进 `api/ledger.ts` 的 `refund` 里）会让**网络超时后的重试退两次钱**。
 */
export function newIdempotencyKey(): string {
  const webcrypto = globalThis.crypto;
  if (typeof webcrypto?.randomUUID === 'function') return webcrypto.randomUUID();

  const bytes = new Uint8Array(16);
  if (typeof webcrypto?.getRandomValues === 'function') {
    webcrypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  // RFC 4122：版本位 4（0100）、变体位 10xx
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
